import React, { useMemo } from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline } from './sectionHelpers';

/** Convert a YouTube or Vimeo watch URL to an embeddable src URL. */
function toEmbedUrl(videoUrl: string, autoplay: boolean): string {
  if (!videoUrl) return '';
  // YouTube: youtube.com/watch?v=ID  or  youtu.be/ID
  const ytMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (ytMatch) {
    const params = autoplay ? 'autoplay=1&mute=1' : 'autoplay=0';
    return `https://www.youtube.com/embed/${ytMatch[1]}?${params}`;
  }
  // Vimeo: vimeo.com/ID
  const vmMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
  if (vmMatch) {
    const params = autoplay ? 'autoplay=1&muted=1' : 'autoplay=0';
    return `https://player.vimeo.com/video/${vmMatch[1]}?${params}`;
  }
  // Already an embed URL or unknown — return as-is
  return videoUrl;
}

const VideoSection: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const variant = (c.variant as string) || 'contained';
  const autoplay = c.autoplay === true || c.autoplay === 'true';
  const videoUrl = (c.video_url as string) || '';
  const fallbackImage = (c.fallback_image as string) || '';

  const embedUrl = useMemo(() => toEmbedUrl(videoUrl, autoplay), [videoUrl, autoplay]);

  const maxWidth = variant === 'full_width' ? '100%' : 960;

  return (
    <section className="sb-render sb-render-video-section" style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth, margin: '0 auto' }}>
        {(sfHeadline(c) || true) && (
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.25rem', textAlign: 'center', marginBottom: 12 }}
          >
            {sfHeadline(c, '')}
          </InlineEditText>
        )}

        {(c.description || true) && (
          <InlineEditText
            tag="p"
            controlKey="description"
            style={{ fontSize: '1.125rem', color: '#64748b', textAlign: 'center', marginBottom: 32, maxWidth: 640, margin: '0 auto 32px' }}
          >
            {c.description || ''}
          </InlineEditText>
        )}

        {embedUrl ? (
          <div style={{
            position: 'relative',
            paddingBottom: '56.25%',
            height: 0,
            borderRadius: 'var(--brand-card-radius, 12px)',
            overflow: 'hidden',
          }}>
            <iframe
              src={embedUrl}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0,
              }}
              allowFullScreen
              allow="autoplay; encrypted-media"
              title={sfHeadline(c) as string || 'Video'}
            />
          </div>
        ) : fallbackImage ? (
          <img
            src={fallbackImage}
            alt={sfHeadline(c) as string || 'Video thumbnail'}
            style={{
              width: '100%',
              borderRadius: 'var(--brand-card-radius, 12px)',
              display: 'block',
            }}
          />
        ) : (
          <div style={{
            background: '#f1f5f9',
            borderRadius: 'var(--brand-card-radius, 12px)',
            aspectRatio: '16/9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8',
            fontSize: '0.875rem',
          }}>
            Enter a YouTube or Vimeo URL to embed a video
          </div>
        )}
      </div>
    </section>
  );
};

export default VideoSection;
